import * as Configuration from '../app.constants.js';

export class AsyncRequestService {
  constructor($http, $q, $rootScope, $interval) {
    this._$http = $http;
    this._$q = $q;
    this._$rootScope = $rootScope;

    this._pendingWsResponses = {};
    this._pendingRequests = {};
    this._pollPendingRunning = false;
    this._intervalId = $interval(() => this._pollPendingRequests(), 1000);
    this._$rootScope.$on('ws.user.async-request', (event, wsEvent) => this._websocketCallback(event, wsEvent));

    // The WS notifs that arrive via the subscription above
    // include async requests triggered by this class, but also those triggered by the angular side of the app
    // (that is, in src/app/core/async-request/async-request.service.ts)
    // Hence, in order to avoid WS notifs accumulating and causing a memory leak,
    // we clear the pool periodically. In the unlikely event that we clear a notif that was triggered by this class,
    // the polling mechanism will fetch the response a couple of seconds later anyway.
    this._intervalId = $interval(() => this._pendingWsResponses = {}, 10000);
  }

  _async_url(companyId, requestId) {
    return Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/async-requests/' + requestId;
  }

  _downloadResponse(pendingRequest, requestId) {
    if (pendingRequest.isRunning) {
      return;
    }
    pendingRequest.isRunning = true;
    this._$http.get(this._async_url(pendingRequest.companyId, requestId)).then(
      response => {
        pendingRequest.isRunning = false;
        let asyncResponse = response.data;
        if (response.status === 202) {
          pendingRequest.backoff *= 2;
          if (pendingRequest.backoff > 600) {
            pendingRequest.backoff = 600;
          }
          pendingRequest.delayUntilRetry = Math.floor(Math.random() * pendingRequest.backoff);
          if (pendingRequest.delayUntilRetry < 2) {
            pendingRequest.delayUntilRetry = 2;
          }
        } else if (response.status === 200 && asyncResponse.status === 200) {
              this._doResolve(requestId, asyncResponse);
        } else {
          this._doReject(requestId, asyncResponse);
        }
      },
      error => {
        pendingRequest.isRunning = false;
        this._doReject(requestId);
      }
    );
  }

  _queuePendingRequest(companyId, url, requestId, resolve, reject) {
    let pendingRequest = {
      url: url,
      requestId: requestId,
      companyId: companyId,
      reject: reject,
      resolve: resolve,
      backoff: 2,
      isRunning: false,
      delayUntilRetry: 2
    };
    this._pendingRequests[requestId] = pendingRequest;
    if (requestId in this._pendingWsResponses) {
      // This can happen if we have received and processed
      // the websoket notification associated with the response 
      // to this request faster than this request has completed.
      this._handleWsResponse(pendingRequest, requestId, this._pendingWsResponses[requestId]);
      delete this._pendingWsResponses[requestId];
    }
  }


  _pollPendingRequests() {
    if (this._pollPendingRunning) {
      return;
    }
    this._pollPendingRunning = true;
    for (let requestId in this._pendingRequests) {
      let pendingRequest = this._pendingRequests[requestId];
      pendingRequest.delayUntilRetry -= 1;
      if (pendingRequest.delayUntilRetry > 0) {
        continue;
      }
      this._downloadResponse(pendingRequest, requestId);
    }
    this._pollPendingRunning = false;
  }

  _clear(requestId) {
    if (this._pendingRequests[requestId]) {
      delete this._pendingRequests[requestId];
    }
  }
  _doResolve(requestId, value) {
    if (this._pendingRequests[requestId]) {
      this._pendingRequests[requestId].resolve(value);
      this._clear(requestId);
    }
  }
  _doReject(requestId, value) {
    if (this._pendingRequests[requestId]) {
      if (value === undefined) {
        value = { status: -1 };
      }
      this._pendingRequests[requestId].reject(value);
      this._clear(requestId);
    }
  }

  _handleWsResponse(pendingRequest, requestId, wsEvent) {
    if (wsEvent.data.hasOwnProperty('response')) {
      // inline response in websocket
      if (pendingRequest.isRunning) {
        return;
      }
      pendingRequest.isRunning = true;
      let response = wsEvent.data.response
      if (response.status == 200) {
        this._doResolve(requestId, response);
      } else {
        this._doReject(requestId, response);
      }
      pendingRequest.isRunning = false;
    } else {
      this._downloadResponse(pendingRequest, requestId);
    }
  }

  _websocketCallback(event, wsEvent) {
    let requestId = wsEvent.data.id;
    if (this._pendingRequests[requestId]) {
      let pendingRequest = this._pendingRequests[requestId];
      this._handleWsResponse(pendingRequest, requestId, wsEvent);
    } else {
      this._pendingWsResponses[requestId] = wsEvent;
    }
  }

  post(companyId, url, data, headers) {
    let config = {
      url: url,
      method: "POST",
      data: data,
      headers: headers
    };
    return this._doMethod(companyId, url, config);
  }
  get(companyId, url, params) {
    let config = {
      url: url,
      method: "GET",
      params: params
    };
    return this._doMethod(companyId, url, config);
  }
  delete(companyId, url) {
    let config = {
      url: url,
      method: "DELETE"
    };
    return this._doMethod(companyId, url, config);
  }
  __doMethod(companyId, url, config, resolve, reject) {
    return this._$http(config).then(
      startResponse => {
        // server force synchronous call
        if (!startResponse.data.is_really_async) {
          let response = startResponse.data.sync_response;
          if (response.status === 200) {
            resolve(response);
          } else {
            reject(response);
          }
        } else {
          let requestId = startResponse.data.async_response.id;
          this._queuePendingRequest(companyId, url, requestId, resolve, reject);
        }
      },
      errorResponse => {
        if (errorResponse.status == 429) {
          // We have submitted too many requests. Let's wait for the responses
          let previousPendingRequestId = errorResponse.data.pending_request.id;
          let previousPendingRequest = this._pendingRequests[previousPendingRequestId];
          if (previousPendingRequest) {
            // We silently discard previous requests because this is what we did in the
            // previous version of this code and this is what callers of this code
            // expect us to do.
          }
          this._$q((requestResolve, requestReject) => {
            this._queuePendingRequest(companyId, url, previousPendingRequestId, requestResolve, requestReject)
          }).finally(() => {
            this.__doMethod(companyId, url, config, resolve, reject);
          });
        } else {
          reject({ status: -1 });
        }
      }
    );
  }
  _doMethod(companyId, url, config) {
    for (let requestId in this._pendingRequests) {
      let pendingRequest = this._pendingRequests[requestId];
      if (pendingRequest.url === url) {
        this._clear(requestId);
      }
    }
    return this._$q((resolve, reject) => {
      return this.__doMethod(companyId, url, config, resolve, reject);
    });

  }
  static AsyncRequestFactory($http, $q, $rootScope, $interval) {
    return new AsyncRequestService($http, $q, $rootScope, $interval);
  }
}

AsyncRequestService.AsyncRequestFactory.$inject = ['$http', '$q', '$rootScope', '$interval'];

